/**
 * Demo Center status strip (WQ-348, DC-17): one chip per server component, visible to
 * every entitled user. State is always written out (never colour alone).
 */
import React from 'react';
import {
  formatBytes,
  shortSha,
  type ComponentState,
  type DemoComponent,
  type DemoStatus,
} from '../../services/demoStatusService';

export const STATE_TEXT: Record<ComponentState, string> = {
  ok: 'Running',
  degraded: 'Degraded',
  down: 'Unavailable',
  not_live: 'Not live yet',
};
const STATE_BADGE: Record<ComponentState, string> = {
  ok: 'badge badge-success',
  degraded: 'badge badge-warning',
  down: 'badge badge-error',
  not_live: 'badge badge-secondary',
};

function when(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

const Chip: React.FC<{ component: DemoComponent; checkedAt: string; diskFreePct: number | null }> = ({
  component,
  checkedAt,
  diskFreePct,
}) => {
  const failing = component.checks.find((c) => !c.ok);
  const store = component.store;
  return (
    <div
      className="card"
      data-testid={`demo-status-${component.id}`}
      data-state={component.state}
      style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, padding: 'var(--space-3, 12px)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
        <strong style={{ color: 'var(--text-primary)' }}>{component.label}</strong>
        <span className={STATE_BADGE[component.state]}>{STATE_TEXT[component.state]}</span>
      </div>
      <div style={{ fontSize: 'var(--text-sm, 13px)', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
        {component.id === 'host'
          ? <>Disk free: {diskFreePct === null ? '—' : `${diskFreePct}%`}</>
          : <>Version <code>{shortSha(component.version?.sha)}</code>{when(component.version?.built_at) ? ` · built ${when(component.version?.built_at)}` : ''}</>}
      </div>
      {(component.state === 'degraded' || component.state === 'down') && (
        <div data-testid={`demo-status-${component.id}-detail`} style={{ fontSize: 'var(--text-sm, 13px)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {failing ? `${failing.name}: ${failing.detail || 'failed'}` : component.detail}
        </div>
      )}
      {store && (
        <div data-testid={`demo-status-${component.id}-store`} style={{ fontSize: 'var(--text-sm, 13px)', color: 'var(--text-secondary)' }}>
          {store.available
            ? <>Store {formatBytes(store.bytes ?? 0)} · {store.uploads ?? 0} uploads · {store.sites ?? 0} mines · {store.archived ?? 0} archived{when(store.as_of) ? ` · as of ${when(store.as_of)}` : ''}</>
            : <>Store size unavailable{store.error ? `: ${store.error}` : ''}</>}
        </div>
      )}
      <div style={{ fontSize: 'var(--text-xs, 12px)', color: 'var(--text-tertiary, var(--text-secondary))' }}>
        Checked {when(checkedAt) ?? '—'}
      </div>
    </div>
  );
};

const DemoStatusStrip: React.FC<{ status: DemoStatus }> = ({ status }) => (
  <div
    role="status"
    aria-label="Demo Center server status"
    data-testid="demo-status-strip"
    style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3, 12px)', marginBottom: 'var(--space-4, 16px)' }}
  >
    {status.components.map((c) => (
      <Chip key={c.id} component={c} checkedAt={status.checked_at} diskFreePct={status.host?.disk_free_pct ?? null} />
    ))}
  </div>
);

export default DemoStatusStrip;
